import React, { useState, useEffect } from 'react';
import {
  Box,
  FormControl,
  Select,
  MenuItem,
  InputLabel,
  Autocomplete,
  TextField,
  IconButton,
  Typography,
} from '@mui/material';
import { Delete as DeleteIcon } from '@mui/icons-material';
import { FieldDefinition, Operator, Expression as ExpressionType, SelectOption } from '../types';
import { ValueInput } from './ValueInput';

interface ExpressionProps {
  expression: ExpressionType;
  groupId: string;
  expressionId: string;
  onUpdate: (groupId: string, expressionId: string, expression: Partial<ExpressionType>) => void;
  onRemove: (groupId: string, expressionId: string) => void;
  fetchFieldCatalog: (searchTerm?: string) => Promise<FieldDefinition[]>;
  fetchResourceValues: (resourceId: string, searchTerm?: string) => Promise<SelectOption[]>;
  locale: { [key: string]: string };
  showRemoveButton?: boolean;
}

export const Expression: React.FC<ExpressionProps> = ({
  expression,
  groupId,
  expressionId,
  onUpdate,
  onRemove,
  fetchFieldCatalog,
  fetchResourceValues,
  locale,
  showRemoveButton = true,
}) => {
  const [fieldOptions, setFieldOptions] = useState<FieldDefinition[]>([]);
  const [fieldsLoading, setFieldsLoading] = useState(false);
  const [fieldSearchTerm, setFieldSearchTerm] = useState('');

  // Load field options
  useEffect(() => {
    setFieldsLoading(true);
    fetchFieldCatalog(fieldSearchTerm)
      .then(setFieldOptions)
      .catch(console.error)
      .finally(() => setFieldsLoading(false));
  }, [fieldSearchTerm, fetchFieldCatalog]);

  // Get available operators for the selected field based on field type
  const getOperatorOptions = (): { value: Operator; label: string }[] => {
    if (!expression.field) return [];

    const operatorLabels: Record<Operator, string> = {
      equals: 'Equals',
      not_equals: 'Not Equals',
      greater_than: 'Greater Than',
      less_than: 'Less Than',
      present: 'Is Present',
      not_present: 'Is Not Present',
      includes: 'Contains',
      not_includes: 'Does Not Contain',
      between: 'Between',
    };

    // Dynamically determine operators based on field type (from hub-server condition engine analysis)
    const getOperatorsForFieldType = (fieldType: string): Operator[] => {
      switch (fieldType) {
        case 'text':
          return ['equals', 'not_equals', 'includes', 'present', 'not_present'];
        case 'number':
          return ['equals', 'not_equals', 'greater_than', 'less_than', 'between', 'present', 'not_present'];
        case 'currency':
          return ['equals', 'not_equals', 'greater_than', 'less_than', 'between', 'present', 'not_present'];
        case 'date':
          return ['equals', 'not_equals', 'between', 'present', 'not_present'];
        case 'boolean':
          return ['equals', 'not_equals', 'present', 'not_present'];
        case 'select':
          return ['equals', 'not_equals', 'present', 'not_present'];
        case 'multiselect':
          return ['includes', 'not_includes', 'present', 'not_present'];
        default:
          return ['present', 'not_present'];
      }
    };

    const operators = getOperatorsForFieldType(expression.field.fieldType);
    
    return operators.map(op => ({
      value: op,
      label: operatorLabels[op] || op,
    }));
  };

  const handleFieldChange = (field: FieldDefinition | null) => {
    if (field) {
      onUpdate(groupId, expressionId, { field });
    } else {
      onUpdate(groupId, expressionId, { field: undefined, operator: undefined, value: [] });
    }
  };

  const handleOperatorChange = (operator: Operator) => {
    onUpdate(groupId, expressionId, { operator });
  };

  const handleValueChange = (value: any[]) => {
    onUpdate(groupId, expressionId, { value });
  };

  const handleRemove = () => {
    onRemove(groupId, expressionId);
  };

  return (
    <Box
      id={`expression-${groupId}-${expressionId}`}
      sx={{
        p: 2,
        border: '1px solid #e0e0e0',
        borderRadius: 1,
        backgroundColor: '#fafafa',
        width: '100%',
        boxSizing: 'border-box',
        display: 'flex',
        flexWrap: 'wrap',
        columnGap: 2,
        rowGap: 1.5,
        alignItems: 'center',
      }}
    >
      {/* Field Selector */}
      <Autocomplete
        freeSolo
        selectOnFocus
        clearOnBlur
        handleHomeEndKeys
        options={fieldOptions}
        getOptionLabel={(option) => (
          typeof option === 'string' ? option : option.label || option.identifier || ''
        )}
        value={expression.field || null}
        onChange={(_, newValue) => {
          if (typeof newValue === 'string') {
            const customField: FieldDefinition = {
              identifier: newValue,
              label: newValue,
              fieldType: 'text',
            };
            handleFieldChange(customField);
          } else {
            handleFieldChange(newValue as FieldDefinition | null);
          }
        }}
        onInputChange={(_, newInputValue) => setFieldSearchTerm(newInputValue)}
        loading={fieldsLoading}
        size="small"
        renderInput={(params) => (
          <TextField
            {...params}
            label="Field"
            placeholder={locale.fieldPlaceholder}
            fullWidth
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {fieldsLoading ? <span style={{ fontSize: '0.75rem' }}>Loading...</span> : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
        isOptionEqualToValue={(option, value) => option.identifier === value.identifier}
      />

      {/* Operator Selector */}
      <FormControl fullWidth size="small" sx={{ flex: '1 1 180px', minWidth: 160 }}>
        <InputLabel>Operator</InputLabel>
        <Select
          value={expression.operator || ''}
          onChange={(e) => handleOperatorChange(e.target.value as Operator)}
          label="Operator"
          disabled={!expression.field}
        >
          {getOperatorOptions().map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {/* Value Input */}
      {expression.field && expression.operator ? (
        <ValueInput
          field={expression.field}
          operator={expression.operator}
          value={expression.value}
          onChange={handleValueChange}
          fetchResourceValues={fetchResourceValues}
          locale={locale}
        />
      ) : (
        <Box sx={{ height: 40, display: 'flex', alignItems: 'center', flex: '1 1 240px', minWidth: 200 }}>
          <Typography variant="caption" color="text.secondary">
            {locale.valuePlaceholder}
          </Typography>
        </Box>
      )}

      {/* Controls */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: '0 0 auto', ml: 'auto' }}>
        {showRemoveButton && (
          <IconButton
            onClick={handleRemove}
            size="small"
            color="error"
            title={locale.removeButton}
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        )}
        <Box
          sx={{
            width: 10,
            height: 10,
            borderRadius: '50%',
            backgroundColor: expression.isCompleted ? 'success.main' : 'error.main',
          }}
          title={expression.isCompleted ? 'Expression Complete' : 'Expression Incomplete'}
        />
      </Box>
    </Box>
  );
};